// frontend/src/pages/AdminPage.jsx

import { Fragment, useEffect, useState } from 'react'
import { Layout } from '../components/Layout/Layout'
import { Card } from '../components/Common/Card'
import { adminAPI } from '../services/api'
import {
  Users, CreditCard, TrendingUp, FolderOpen, Search,
  CheckCircle, Clock, XCircle, Shield, Loader, ChevronDown, ChevronUp, Crown
} from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_LABELS = {
  pending: { label: 'Ожидает', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  waiting_for_capture: { label: 'Обработка', color: 'bg-blue-100 text-blue-700', icon: Clock },
  succeeded: { label: 'Оплачен', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  canceled: { label: 'Отменён', color: 'bg-red-100 text-red-700', icon: XCircle },
}

const PaymentStatusBadge = ({ status }) => {
  const info = STATUS_LABELS[status] || STATUS_LABELS.pending
  const Icon = info.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${info.color}`}>
      <Icon size={12} />
      {info.label}
    </span>
  )
}

// ============ СТАТИСТИКА ============

const StatsCards = ({ stats }) => {
  if (!stats) return null

  const cards = [
    { label: 'Пользователей', value: stats.total_users, sub: `${stats.active_users} активных`, icon: Users, color: 'blue' },
    { label: 'Выручка', value: `${stats.total_revenue.toLocaleString('ru-RU')} ₽`, sub: `${stats.payments_succeeded} платежей`, icon: TrendingUp, color: 'green' },
    { label: 'Активных подписок', value: stats.active_subscriptions, sub: `${stats.payments_pending} ожидают оплаты`, icon: Crown, color: 'purple' },
    { label: 'Проектов', value: stats.total_projects, sub: 'всего в системе', icon: FolderOpen, color: 'orange' },
  ]

  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map(({ label, value, sub, icon: Icon, color }) => (
        <div key={label} className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${colorMap[color]}`}>
              <Icon size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">{sub}</p>
        </div>
      ))}
    </div>
  )
}

// ============ ТАБЛИЦА ПОЛЬЗОВАТЕЛЕЙ ============

const UsersTable = () => {
  const [users, setUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [expandedUser, setExpandedUser] = useState(null)
  const [userPayments, setUserPayments] = useState({})

  const loadUsers = async (searchQuery = '') => {
    setLoading(true)
    try {
      const response = await adminAPI.getUsers(
        searchQuery ? { search: searchQuery } : {}
      )
      setUsers(response.data.users || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      console.error('Load users error:', error)
      toast.error(error.response?.data?.detail || 'Не удалось загрузить пользователей')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    loadUsers(search)
  }

  const toggleUserPayments = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null)
      return
    }
    setExpandedUser(userId)

    if (!userPayments[userId]) {
      try {
        const response = await adminAPI.getUserPayments(userId)
        setUserPayments(prev => ({ ...prev, [userId]: response.data.payments || [] }))
      } catch (error) {
        console.error('Load user payments error:', error)
        toast.error('Не удалось загрузить платежи пользователя')
      }
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Users size={20} className="text-blue-600" />
          Пользователи ({total})
        </h3>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по email или имени..."
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm flex items-center gap-1"
          >
            <Search size={16} />
            Найти
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader className="animate-spin text-blue-600" size={32} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-3 font-semibold">Пользователь</th>
                <th className="text-center py-3 px-3 font-semibold">Регистрация</th>
                <th className="text-center py-3 px-3 font-semibold">Последний вход</th>
                <th className="text-center py-3 px-3 font-semibold">Проектов</th>
                <th className="text-center py-3 px-3 font-semibold">Подписка</th>
                <th className="text-right py-3 px-3 font-semibold">Оплачено</th>
                <th className="text-center py-3 px-3 font-semibold">Платежи</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <Fragment key={user.id}>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">
                            {(user.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 flex items-center gap-1">
                            {user.first_name || user.username || user.email.split('@')[0]}
                            {user.last_name && ` ${user.last_name}`}
                            {user.is_admin && (
                              <Shield size={14} className="text-purple-600" title="Администратор" />
                            )}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-3 px-3 text-gray-600 text-xs">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : '-'}
                    </td>
                    <td className="text-center py-3 px-3 text-gray-600 text-xs">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString('ru-RU') : '-'}
                    </td>
                    <td className="text-center py-3 px-3 font-medium">{user.projects_count}</td>
                    <td className="text-center py-3 px-3">
                      {user.subscription ? (
                        <span className="inline-flex flex-col items-center px-2 py-1 bg-green-50 text-green-700 text-xs rounded">
                          <span className="font-medium">{user.subscription.plan_name}</span>
                          <span className="text-green-600">
                            до {new Date(user.subscription.expires_at).toLocaleDateString('ru-RU')}
                          </span>
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">Нет</span>
                      )}
                    </td>
                    <td className="text-right py-3 px-3 font-semibold text-green-600">
                      {user.total_paid > 0 ? `${user.total_paid.toLocaleString('ru-RU')} ₽` : '-'}
                    </td>
                    <td className="text-center py-3 px-3">
                      {user.payments_count > 0 ? (
                        <button
                          onClick={() => toggleUserPayments(user.id)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs font-medium"
                        >
                          {user.payments_count} шт.
                          {expandedUser === user.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">0</span>
                      )}
                    </td>
                  </tr>

                  {/* Развёрнутые платежи пользователя */}
                  {expandedUser === user.id && (
                    <tr>
                      <td colSpan="7" className="bg-gray-50 px-6 py-3">
                        {!userPayments[user.id] ? (
                          <div className="flex justify-center py-3">
                            <Loader className="animate-spin text-blue-600" size={20} />
                          </div>
                        ) : userPayments[user.id].length === 0 ? (
                          <p className="text-gray-500 text-xs text-center py-2">Платежей нет</p>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500">
                                <th className="text-left py-1 px-2">Дата</th>
                                <th className="text-left py-1 px-2">Тариф</th>
                                <th className="text-right py-1 px-2">Сумма</th>
                                <th className="text-center py-1 px-2">Статус</th>
                                <th className="text-left py-1 px-2">ID ЮKassa</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userPayments[user.id].map((p) => (
                                <tr key={p.id} className="border-t border-gray-200">
                                  <td className="py-2 px-2">{new Date(p.created_at).toLocaleString('ru-RU')}</td>
                                  <td className="py-2 px-2 font-medium">{p.plan_name}</td>
                                  <td className="text-right py-2 px-2 font-semibold">
                                    {p.amount.toLocaleString('ru-RU')} ₽
                                  </td>
                                  <td className="text-center py-2 px-2">
                                    <PaymentStatusBadge status={p.status} />
                                  </td>
                                  <td className="py-2 px-2 font-mono text-gray-400">
                                    {p.yookassa_payment_id || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <p className="text-center text-gray-500 py-8">Пользователи не найдены</p>
          )}
        </div>
      )}
    </Card>
  )
}

// ============ ТАБЛИЦА ВСЕХ ПЛАТЕЖЕЙ ============

const PaymentsTable = () => {
  const [payments, setPayments] = useState([])
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(true)

  const loadPayments = async (filter = '') => {
    setLoading(true)
    try {
      const params = filter ? { payment_status: filter } : {}
      const response = await adminAPI.getPayments(params)
      setPayments(response.data.payments || [])
    } catch (error) {
      console.error('Load payments error:', error)
      toast.error('Не удалось загрузить платежи')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPayments()
  }, [])

  const handleFilterChange = (e) => {
    const value = e.target.value
    setStatusFilter(value)
    loadPayments(value)
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <CreditCard size={20} className="text-green-600" />
          Все платежи
        </h3>
        <select
          value={statusFilter}
          onChange={handleFilterChange}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Все статусы</option>
          <option value="succeeded">Оплаченные</option>
          <option value="pending">Ожидают оплаты</option>
          <option value="canceled">Отменённые</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader className="animate-spin text-blue-600" size={32} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-3 font-semibold">Дата</th>
                <th className="text-left py-3 px-3 font-semibold">Пользователь</th>
                <th className="text-left py-3 px-3 font-semibold">Тариф</th>
                <th className="text-right py-3 px-3 font-semibold">Сумма</th>
                <th className="text-center py-3 px-3 font-semibold">Статус</th>
                <th className="text-center py-3 px-3 font-semibold">Оплачен</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-3 text-gray-600 text-xs">
                    {new Date(payment.created_at).toLocaleString('ru-RU')}
                  </td>
                  <td className="py-3 px-3">{payment.user_email}</td>
                  <td className="py-3 px-3 font-medium">{payment.plan_name}</td>
                  <td className="text-right py-3 px-3 font-semibold">
                    {payment.amount.toLocaleString('ru-RU')} ₽
                  </td>
                  <td className="text-center py-3 px-3">
                    <PaymentStatusBadge status={payment.status} />
                  </td>
                  <td className="text-center py-3 px-3 text-gray-600 text-xs">
                    {payment.paid_at ? new Date(payment.paid_at).toLocaleString('ru-RU') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {payments.length === 0 && (
            <p className="text-center text-gray-500 py-8">Платежи не найдены</p>
          )}
        </div>
      )}
    </Card>
  )
}

// ============ ГЛАВНАЯ СТРАНИЦА АДМИНКИ ============

export const AdminPage = () => {
  const [stats, setStats] = useState(null)
  const [activeTab, setActiveTab] = useState('users')

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await adminAPI.getStats()
        setStats(response.data.stats)
      } catch (error) {
        console.error('Load stats error:', error)
        if (error.response?.status === 403) {
          toast.error('Доступ запрещён: требуются права администратора')
        }
      }
    }
    loadStats()
  }, [])

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Shield size={28} className="text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Панель администратора</h1>
            <p className="text-sm text-gray-600">Пользователи, платежи и статистика</p>
          </div>
        </div>

        {/* Статистика */}
        <StatsCards stats={stats} />

        {/* Табы */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === 'users'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Пользователи
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === 'payments'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Платежи
          </button>
        </div>

        {activeTab === 'users' && <UsersTable />}
        {activeTab === 'payments' && <PaymentsTable />}
      </div>
    </Layout>
  )
}

export default AdminPage
